import React, { useEffect, useState } from 'react'
import { useTheme } from '@/context/ThemeContext'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  MessageSquare,
  CalendarDays,
  Mail,
  TrendingUp,
  Settings,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getMyWorkspaces } from '@/features/onboarding/services/workspaceApi'

interface SidebarProps {
  user?: { name?: string; email?: string }
  initialExpanded?: boolean
}

const navItems = [
  { href: '/dashboard', label: 'Home', icon: <Home className="w-5 h-5" /> },
  { href: '/dashboard/chat', label: 'Chat', icon: <MessageSquare className="w-5 h-5" /> },
  { href: '/dashboard/calendar', label: 'Calendar', icon: <CalendarDays className="w-5 h-5" /> },
  { href: '/dashboard/sales', label: 'Sales', icon: <TrendingUp className="w-5 h-5" /> },
  {
    href: '/dashboard/settings',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
  },
]

const SIDEBAR_EXPANDED_KEY = 'sidebar:expanded'

export const Sidebar: React.FC<SidebarProps> = ({ user: userProp, initialExpanded = false }) => {
  const { currentTheme } = useTheme()
  const [isExpanded, setIsExpanded] = React.useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_EXPANDED_KEY)
      if (stored !== null) return stored === 'true'
    } catch {}
    return initialExpanded
  })
  const [localUser, setLocalUser] = useState<{ name?: string; email?: string } | null>(null)
  const [workspaceName, setWorkspaceName] = useState<string | null>(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user')
      if (raw) setLocalUser(JSON.parse(raw))
    } catch {
      setLocalUser(null)
    }
  }, [])

  useEffect(() => {
    getMyWorkspaces()
      .then((ws) => {
        if (ws[0]) {
          setWorkspaceName(ws[0].name)
        }
      })
      .catch(() => {})
  }, [])

  const user = userProp ?? localUser

  const location = useLocation()
  const pathname = location.pathname

  const toggleSidebar = () => {
    setIsExpanded((s) => {
      const next = !s
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(next))
      } catch {}
      return next
    })
  }

  return (
    <div
      className={`hidden md:flex relative flex-col h-full shrink-0 transition-[width] duration-300 ease-in-out overflow-hidden
        ${isExpanded ? 'w-64' : 'w-[72px]'}`}
      style={{
        background: currentTheme.sidebarBg,
        borderRight: `1px solid ${currentTheme.sidebarBorder}`,
      }}
    >
      {/* Logo */}
      <div
        className={`flex items-center gap-3 px-4 pt-6 pb-5 ${isExpanded ? '' : 'justify-center'}`}
      >
        <img
          src="/Zeph-White-Logo.png"
          alt="Zeph logo"
          className="object-contain w-9 h-9 shrink-0 ml-2.5"
        />
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-w-[170px] opacity-100' : 'max-w-0 opacity-0'
          }`}
        >
          <span
            className="text-base font-bold tracking-widest whitespace-nowrap"
            style={{ color: currentTheme.sidebarActiveText }}
          >
            ZEPH.AI
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-3 mb-4 h-px" style={{ backgroundColor: currentTheme.sidebarBorder }} />

      {/* Nav */}
      <nav className="flex-1 w-full px-3 overflow-y-auto">
        <ul className="space-y-1.5">
          {navItems.map(({ href, label, icon }) => {
            const isActive = pathname === href
            return (
              <li key={href}>
                <Link
                  to={href}
                  title={!isExpanded ? label : undefined}
                  className={`relative flex items-center rounded-lg h-12 px-3 transition-colors duration-150 group
                    ${isExpanded ? '' : 'justify-center'}`}
                  style={{
                    backgroundColor: isActive ? currentTheme.sidebarActive : 'transparent',
                    color: isActive ? currentTheme.sidebarActiveText : currentTheme.sidebarText,
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = currentTheme.sidebarHover
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  {isActive && (
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-[2px]"
                      style={{ backgroundColor: currentTheme.accent }}
                    />
                  )}

                  <div className="flex items-center justify-center w-6 h-6 shrink-0">
                    {icon ? icon : <div className="w-5 h-5 bg-gray-400 rounded-sm" />}
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-w-[170px] opacity-100 ml-3' : 'max-w-0 opacity-0 ml-0'
                    }`}
                  >
                    <span className="text-[15px] font-medium whitespace-nowrap leading-none">
                      {label}
                    </span>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* User info */}
      <div className="px-3 pb-3">
        <Link
          to="/dashboard/settings?tab=workspace"
          className={`flex items-center rounded-lg px-2 py-2.5 gap-3 transition-colors duration-150 ${isExpanded ? '' : 'justify-center'}`}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = currentTheme.sidebarHover }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
        >
          <Avatar className="w-9 h-9 shrink-0">
            <AvatarImage src="/Assets/avatar.jpg" alt={user?.name || 'User'} />
            <AvatarFallback style={{ backgroundColor: currentTheme.accent, color: '#fff', fontSize: 13, fontWeight: 700 }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-w-[170px] opacity-100' : 'max-w-0 opacity-0'
            }`}
          >
            <p className="text-sm font-semibold whitespace-nowrap leading-tight truncate max-w-[140px]" style={{ color: currentTheme.sidebarActiveText }}>
              {user?.name || 'Guest'}
            </p>
            {workspaceName && (
              <p className="text-xs whitespace-nowrap leading-tight mt-0.5 truncate max-w-[140px]" style={{ color: currentTheme.sidebarText }}>
                {workspaceName}
              </p>
            )}
          </div>
        </Link>
      </div>

      {/* Collapse / expand toggle */}
      <div className="px-3 pb-4 pt-2">
        <div className="h-px mb-3" style={{ backgroundColor: currentTheme.sidebarBorder }} />
        <button
          onClick={toggleSidebar}
          title={!isExpanded ? 'Expand sidebar' : undefined}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-expanded={isExpanded}
          className={`flex items-center w-full rounded-lg h-11 px-3 transition-colors duration-150
            ${isExpanded ? '' : 'justify-center'}`}
          style={{ color: currentTheme.sidebarText }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.sidebarHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <div className="flex items-center justify-center w-6 h-6 shrink-0">
            {isExpanded ? (
              <PanelRightOpen className="w-5 h-5" />
            ) : (
              <PanelRightClose className="w-5 h-5" />
            )}
          </div>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-w-[170px] opacity-100 ml-3' : 'max-w-0 opacity-0 ml-0'
            }`}
          >
            <span className="text-[15px] font-medium whitespace-nowrap leading-none">Collapse</span>
          </div>
        </button>
      </div>
    </div>
  )
}

export const MemoizedSidebar = React.memo(Sidebar)

export default MemoizedSidebar
